// Builds the "Programme Context" block injected into the LLM prompt when a
// session is generated for a programme slot (#54). Gives the model the
// programme's periodisation framework constraints plus real awareness of the
// surrounding sessions — what was done, what this slot is, and what comes
// next — so consecutive weeks progress instead of repeating verbatim.

import {
  getFramework,
  isDeloadSlot,
  slotPlan,
  isCardioKind,
  DELOAD_CONSTRAINT,
} from "../config/periodisation";
import { summarisePerformance, progressionAdvice } from "./liftPerformance";
import { lastWeekGuidance } from "./programmeWeeks";

// Walk a plan tree collecting Work-node labels (unique, order preserved).
export const summariseExercises = (planData, cap = 12) => {
  const labels = [];
  const seen = new Set();
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "Work" && node.label && !seen.has(node.label)) {
      seen.add(node.label);
      labels.push(node.label);
    }
    (node.children || []).forEach(walk);
  };
  walk(planData);
  if (labels.length > cap) {
    return `${labels.slice(0, cap).join(", ")} (+${labels.length - cap} more)`;
  }
  return labels.join(", ");
};

const STATUS_MARKS = {
  completed: "done",
  missed: "missed",
  pending: "planned",
};

const describeSlot = (slot, { isTarget = false } = {}) => {
  const name = slot.label || slot.scheduled_label || "Session";
  if (isTarget) return `${name} ← THIS SESSION`;
  const mark = STATUS_MARKS[slot.status] || slot.status;
  const dur =
    slot.status === "completed" && slot.duration_minutes
      ? `, ${slot.duration_minutes} min`
      : "";
  return `${name} (${mark}${dur})`;
};

const describeWeek = (week, targetSession) =>
  (week?.sessions || [])
    .map((s) => describeSlot(s, { isTarget: s.id === targetSession?.id }))
    .join("; ");

const forMinutes = (minutes) => (minutes ? ` (about ${minutes} min in total)` : "");

// Activity counts a session by its title (utils/trainingBalance.js), so each
// cardio day asks for a title that names what it is.
const DAY_KIND_CONSTRAINTS = {
  zone2: (minutes) =>
    `THIS IS A ZONE 2 DAY${forMinutes(minutes)}: continuous easy aerobic work at a conversational, nasal-breathing pace (Zone 2) — walking uphill, cycling, rowing, running or similar. Keep it steady; no intervals, no lifting. A short easy build-up at the start is enough warm-up. Put "Zone 2" in the session's label.`,
  zone2_sprints: (minutes) =>
    `THIS IS A ZONE 2 + SPRINTS DAY${forMinutes(minutes)}: continuous easy Zone 2 work (conversational, nasal breathing) for most of the session, then ONE short sprint block near the end — 4-6 all-out efforts of 20-30 s, each followed by easy recovery until breathing settles (about 2-3 min of all-out work in total) — then a few easy minutes to finish. Use a machine from the user's equipment list that can be pushed all-out safely (for example a bike, rower or ski erg, but only if it's on the list), or hill runs if there's none. No lifting. Put "Zone 2 + Sprints" in the session's label.`,
  vo2max: (minutes) =>
    `THIS IS A VO2MAX DAY${forMinutes(minutes)}: aerobic power intervals — warm up, then 3-4 rounds of 3-4 min at 90-95% of max heart rate (hard but sustainable for the whole interval, RPE 8-9), 2-3 min easy between rounds, then cool down. These are NOT all-out sprints. Use a cardio machine from the user's equipment list, or running if there's none; no lifting. Put "VO2max" in the session's label.`,
  intervals: (minutes) =>
    `THIS IS A SPRINT INTERVAL DAY${forMinutes(minutes)}: anaerobic intervals — warm up, then all-out efforts of 20-90 s adding up to about 5-6 min of all-out work, each followed by recovery at least as long as the effort (1:1 to 1:3) so the next one is truly all-out, then cool down. No lifting. Put "Sprint Intervals" in the session's label.`,
  resistance_intervals: () =>
    `This lifting day ends with a short sprint FINISHER. Do ALL the lifting first, to the framework below, then one short all-out block: about 5-6 min of all-out work in 20-30 s efforts with easy recovery between (HIIT rules, anaerobic). Keep it short so it doesn't eat into the lifting. On a lower-body or full-body day use a cardio machine from the user's equipment list rather than running or jumping, to spare the legs (running or jumping only if they have none). Fit both inside the session time. Label the session with the lifting style and the finisher, e.g. "Hypertrophy + Sprint Finisher" or "Strength + Sprint Finisher".`,
};

const CARDIO_DELOAD_CONSTRAINT =
  "THIS IS A DELOAD WEEK: keep the session shorter and easier than usual — easy pace throughout, and for an interval day fewer, less intense efforts. The goal is recovery while staying in motion.";

/**
 * Build the programme-context string for generating `targetSession` inside
 * `programme`. Returns undefined when there is nothing useful to say.
 */
export const buildProgrammeContext = (programme, targetSession) => {
  if (!programme || !targetSession) return undefined;

  const {
    title,
    focus,
    duration_weeks: durationWeeks,
    days_per_week: perWeek,
    progression_style: style,
    notes,
    weeks = [],
  } = programme;
  const weekNumber = targetSession.week_number;
  const framework = getFramework(style);
  const scheduledLabel = targetSession.scheduled_label || "Session";

  const parts = [];
  const { kind, minutes } = slotPlan(programme, targetSession);
  const isCardioDay = isCardioKind(kind);

  // 1. Overview
  parts.push(
    `This session is week ${weekNumber} of ${durationWeeks} in the programme "${title || focus || "training"}"` +
      ` (focus: ${focus || "general fitness"}, ${perWeek} sessions/week` +
      (framework ? `, framework: ${framework.label}` : style ? `, progression: ${style}` : "") +
      `). Slot: "${scheduledLabel}".`,
  );

  // 2. User's programme-level goals/constraints
  if (notes && notes.trim()) {
    parts.push(`Programme goals & constraints from the user: "${notes.trim()}"`);
  }
  // Why this day differs from the usual plan (#92 notes review).
  if (targetSession.plan_note) {
    parts.push(`Note for this day: ${targetSession.plan_note}`);
  }

  // 3. What kind of day this is (#92). The framework's lifting rules apply
  // to lifting days only; Zone 2 and interval days are planned cardio.
  if (DAY_KIND_CONSTRAINTS[kind]) {
    parts.push(DAY_KIND_CONSTRAINTS[kind](minutes));
  }
  if (framework && !isCardioDay) {
    parts.push(framework.llmConstraints);
  }

  // 4. Deload handling
  if (isDeloadSlot(programme, targetSession)) {
    parts.push(isCardioDay ? CARDIO_DELOAD_CONSTRAINT : DELOAD_CONSTRAINT);
  }

  // 4b. How last week went (#94): a light week, illness or an easy restart.
  const guidance = lastWeekGuidance(programme, weekNumber);
  if (guidance) parts.push(guidance);

  // 5. Surrounding schedule: previous, current and next week
  const findWeek = (n) => weeks.find((w) => w.week_number === n);
  const prevWeek = findWeek(weekNumber - 1);
  const currentWeek = findWeek(weekNumber);
  const nextWeek = findWeek(weekNumber + 1);
  const scheduleLines = [];
  if (prevWeek?.sessions?.length) {
    scheduleLines.push(`Last week (W${weekNumber - 1}): ${describeWeek(prevWeek, targetSession)}`);
  }
  if (currentWeek?.sessions?.length) {
    scheduleLines.push(`This week (W${weekNumber}): ${describeWeek(currentWeek, targetSession)}`);
  }
  if (nextWeek?.sessions?.length) {
    scheduleLines.push(
      `Next week (W${weekNumber + 1}): ${(nextWeek.sessions || [])
        .map((s) => s.label || s.scheduled_label || "Session")
        .join("; ")}`,
    );
  }
  if (scheduleLines.length) {
    parts.push(`Surrounding schedule —\n${scheduleLines.join("\n")}`);
  }

  // 6. Same slot in the previous week: the direct progression anchor
  const prevSlot = prevWeek?.sessions?.find(
    (s) => s.day_index === targetSession.day_index,
  );
  if (prevSlot?.status === "completed") {
    // What was actually done (#92 phase 2): the saved session carries the
    // loads used, including mid-session changes, and which sets were done.
    const performed = prevSlot.session_plan_data || prevSlot.plan_data;
    const performance = performed ? summarisePerformance(performed) : null;
    const exercises = performed && !performance ? summariseExercises(performed) : "";
    // With last week's real figures, the load advice below decides whether to
    // add load; without them, a small increment is the default.
    const progressRule = performance
      ? "Build on it per the framework — repeat the core movement patterns and set the loads as the advice below says"
      : "Progress on it per the framework — repeat the core movement patterns with a small load/volume increment";
    parts.push(
      `Last week's same slot ("${prevSlot.label || scheduledLabel}") was completed` +
        (prevSlot.duration_minutes ? ` in ${prevSlot.duration_minutes} min` : "") +
        (exercises ? `. It contained: ${exercises}` : "") +
        `. ${progressRule}; do NOT output an identical session and do NOT switch to unrelated exercises.`,
    );
    if (performance) {
      parts.push(
        `What was actually done last week on this slot (sets x reps @ load):\n` +
          performance.lines.map((line) => `- ${line}`).join("\n") +
          `\nBase this week's loads on these figures, keeping the same main lifts. ` +
          progressionAdvice({
            rating: prevSlot.session_rating,
            allSetsDone: performance.allSetsDone,
            repsShort: performance.repsShort,
            repsKnown: performance.repsKnown,
          }),
      );
    } else if (Number(prevSlot.session_rating) >= 1) {
      parts.push(progressionAdvice({ rating: prevSlot.session_rating, allSetsDone: null }));
    }
  } else if (prevSlot?.status === "missed") {
    parts.push(
      `Last week's same slot was missed — repeat that week's intended stimulus rather than progressing.`,
    );
  }

  // 7. Most recent completed session anywhere in the programme (fatigue /
  // variety awareness), if it isn't the same-slot session already covered.
  const completed = weeks
    .flatMap((w) => w.sessions || [])
    .filter((s) => s.status === "completed" && s.completed_at)
    .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
  const latest = completed[0];
  if (latest && latest.id !== prevSlot?.id) {
    const exercises = latest.plan_data ? summariseExercises(latest.plan_data) : "";
    parts.push(
      `Most recently completed session: "${latest.label || latest.scheduled_label}" (W${latest.week_number})` +
        (exercises ? ` — ${exercises}` : "") +
        `. Balance today's stimulus and fatigue against it.`,
    );
  }

  // 8. Anti-repetition guard, always last
  parts.push(
    `Generate ONLY this slot's session. Keep it distinct from the other sessions this week (their labels are listed above) and progressive relative to previous weeks.`,
  );

  return parts.join("\n\n");
};

export default buildProgrammeContext;

/** First programme session still to do, in programme order. */
export const nextPendingProgrammeSession = (programme) => {
  for (const week of programme?.weeks || []) {
    for (const session of week?.sessions || []) {
      if (session?.status === "pending") {
        return { ...session, week_number: session.week_number ?? week.week_number };
      }
    }
  }
  return null;
};
